import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import {
    Lock,
    LogOut,
    RefreshCw,
    Loader2,
    Clock,
    Phone,
    Stethoscope,
    User,
    Inbox,
    CheckCircle2,
    UserX,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    MessageCircle,
} from "lucide-react";
import { buildWhatsAppLink } from "../lib/whatsapp";
import { AvailabilityShareCard } from "./AvailabilityShareCard";
import { EditContactDialog } from "./EditContact";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_clinic-booking-86/artifacts/lfwsi76p_Logo_CE_fondoBlanco.png";

const PIN_STORAGE_KEY = "ce_staff_pin";
const AUTO_REFRESH_MS = 60000;

// Preset nurses + special markers. "Otra" → opens a free-text input.
const NURSE_OPTIONS = ["Marisol", "Olga", "Jhoana", "Cinthia", "Ana"];
const NURSE_NO_SHOW = "NoSePresento";
const NURSE_OTHER = "Otra";

const SPANISH_DAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const SPANISH_MONTHS = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

function formatDateLong(d) {
    if (!d) return "";
    return `${SPANISH_DAYS[d.getDay()]}, ${d.getDate()} de ${SPANISH_MONTHS[d.getMonth()]} de ${d.getFullYear()}`;
}
function toISODate(d) {
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
}
function fromISODate(s) {
    return new Date(`${s}T00:00:00`);
}
function formatTime12(t) {
    if (!t) return "";
    const [hh, mm] = t.split(":").map(Number);
    const period = hh >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${period}`;
}

function PinForm({ onSuccess }) {
    const [pin, setPin] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!pin.trim()) {
            toast.error("Ingresa el PIN");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/staff/login`, { pin: pin.trim() });
            sessionStorage.setItem(PIN_STORAGE_KEY, pin.trim());
            onSuccess(pin.trim());
            toast.success("Bienvenida");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "PIN incorrecto");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-5 py-12">
            <div className="w-full max-w-md">
                <div className="text-center mb-10">
                    <img
                        src={LOGO_URL}
                        alt="Logo"
                        className="w-20 h-20 rounded-full object-contain bg-white ring-1 ring-border mx-auto mb-4"
                    />
                    <h1 className="font-heading text-3xl font-medium tracking-tight">
                        Citas del consultorio
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        Acceso exclusivo para el personal de enfermería.
                    </p>
                </div>

                <Card className="rounded-3xl border-border bg-card p-8 soft-shadow">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Lock className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <p className="font-heading text-base font-medium">PIN de acceso</p>
                            <p className="text-sm text-muted-foreground">
                                Pídelo a la administración del consultorio.
                            </p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <Input
                            type="password"
                            inputMode="text"
                            autoComplete="off"
                            placeholder="Ingresa el PIN"
                            value={pin}
                            onChange={(e) => setPin(e.target.value)}
                            className="rounded-2xl h-12 bg-background"
                            data-testid="staff-pin-input"
                            autoFocus
                        />
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-12 rounded-2xl hover:-translate-y-0.5 transition-all"
                            data-testid="staff-pin-submit"
                        >
                            {loading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                "Entrar"
                            )}
                        </Button>
                    </form>
                </Card>

                <div className="text-center mt-6">
                    <Link
                        to="/"
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                        ← Volver al inicio
                    </Link>
                </div>
            </div>
        </div>
    );
}

function StaffList({ pin, onLogout }) {
    const [date, setDate] = useState(new Date());
    const [showCalendar, setShowCalendar] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [updatingIds, setUpdatingIds] = useState(new Set());
    const [otraValues, setOtraValues] = useState({});

    // Fetch appointments for the current pin+date. Defined as a stable callback
    // because both the manual refresh button and the auto-refresh interval call it.
    const fetchAppointments = useCallback(
        async ({ silent = false, signal } = {}) => {
            try {
                const res = await axios.get(`${API}/staff/appointments`, {
                    params: { pin, date: toISODate(date) },
                    signal,
                });
                return { ok: true, data: res.data || [] };
            } catch (err) {
                if (axios.isCancel?.(err) || err?.name === "CanceledError") {
                    return { ok: false, cancelled: true };
                }
                if (err?.response?.status === 401) {
                    return { ok: false, unauthorized: true };
                }
                if (!silent) {
                    toast.error("No se pudieron cargar las citas.");
                }
                return { ok: false };
            }
        },
        [pin, date]
    );

    useEffect(() => {
        const controller = new AbortController();
        let intervalId;
        const run = async (silent) => {
            const result = await fetchAppointments({ silent, signal: controller.signal });
            if (result.cancelled) return;
            if (result.unauthorized) {
                toast.error("PIN inválido. Vuelve a iniciar sesión.");
                onLogout();
                return;
            }
            if (result.ok) {
                setAppointments(result.data);
                setLastUpdated(new Date());
            }
            if (!silent) setLoading(false);
        };
        run(false);
        intervalId = setInterval(() => run(true), AUTO_REFRESH_MS);
        return () => {
            controller.abort();
            clearInterval(intervalId);
        };
    }, [fetchAppointments, onLogout]);

    // Manual refresh button handler.
    const load = async (silent = false) => {
        const result = await fetchAppointments({ silent });
        if (result.unauthorized) {
            toast.error("PIN inválido. Vuelve a iniciar sesión.");
            onLogout();
            return;
        }
        if (result.ok) {
            setAppointments(result.data);
            setLastUpdated(new Date());
        }
        if (!silent) setLoading(false);
    };

    const updateContact = async (appt, patient_name, phone, date, time) => {
        await axios.patch(`${API}/staff/appointments/${appt.id}/contact`, {
            pin,
            patient_name,
            phone,
            date,
            time,
        });
        await load(true);
    };

    const setNurse = async (appt, nurseValue) => {
        // nurseValue may be: a preset name, "NoSePresento", a custom string, or null (clear)
        setUpdatingIds((prev) => new Set(prev).add(appt.id));
        const prevSnapshot = appt;
        // optimistic update
        const optimisticAttended = !!nurseValue && nurseValue !== NURSE_NO_SHOW;
        setAppointments((prev) =>
            prev.map((a) =>
                a.id === appt.id
                    ? {
                          ...a,
                          nurse: nurseValue || null,
                          attended: optimisticAttended,
                          attended_at: nurseValue ? new Date().toISOString() : null,
                      }
                    : a
            )
        );
        try {
            const res = await axios.post(
                `${API}/staff/appointments/${appt.id}/attend`,
                { pin, nurse: nurseValue }
            );
            setAppointments((prev) =>
                prev.map((a) => (a.id === appt.id ? res.data : a))
            );
            if (!nurseValue) {
                toast.success("Estado restablecido");
            } else if (nurseValue === NURSE_NO_SHOW) {
                toast.success("Marcado como no se presentó");
            } else {
                toast.success(`Atendida por ${nurseValue}`);
            }
        } catch (err) {
            toast.error("No se pudo actualizar");
            setAppointments((prev) =>
                prev.map((a) => (a.id === appt.id ? prevSnapshot : a))
            );
        } finally {
            setUpdatingIds((prev) => {
                const next = new Set(prev);
                next.delete(appt.id);
                return next;
            });
        }
    };

    const shiftDay = (days) => {
        const d = new Date(date);
        d.setDate(d.getDate() + days);
        setDate(d);
        setShowCalendar(false);
    };

    const stats = useMemo(() => {
        const total = appointments.length;
        const attended = appointments.filter((a) => a.attended).length;
        const noShow = appointments.filter((a) => a.nurse === NURSE_NO_SHOW).length;
        return { total, attended, noShow, pending: total - attended - noShow };
    }, [appointments]);

    const todayISO = toISODate(new Date());
    const dateISO = toISODate(date);
    const isToday = dateISO === todayISO;

    return (
        <div className="min-h-screen bg-background pb-20">
            {/* Header */}
            <header className="sticky top-0 z-30 border-b border-foreground/5 glass">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                    <Link to="/" className="flex items-center gap-3 min-w-0">
                        <img
                            src={LOGO_URL}
                            alt="Logo"
                            className="w-9 h-9 rounded-full object-contain bg-white ring-1 ring-border shrink-0"
                        />
                        <div className="leading-tight min-w-0">
                            <p className="font-heading font-medium text-sm truncate">
                                Citas · Enfermería
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                                Ciudad Enfermería
                            </p>
                        </div>
                    </Link>

                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => load()}
                            className="rounded-2xl"
                            data-testid="staff-refresh-btn"
                        >
                            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onLogout}
                            className="rounded-2xl"
                            data-testid="staff-logout-btn"
                        >
                            <LogOut className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
                {/* Date nav */}
                <div className="flex items-center gap-2 mb-4">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => shiftDay(-1)}
                        className="rounded-2xl shrink-0"
                        data-testid="staff-prev-day"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                        variant={isToday ? "default" : "outline"}
                        onClick={() => setShowCalendar((v) => !v)}
                        className="rounded-2xl flex-1 justify-center font-medium"
                        data-testid="staff-date-toggle"
                    >
                        <CalendarDays className="h-4 w-4 mr-2" />
                        <span className="truncate">
                            {isToday ? "Hoy · " : ""}
                            {formatDateLong(date)}
                        </span>
                    </Button>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => shiftDay(1)}
                        className="rounded-2xl shrink-0"
                        data-testid="staff-next-day"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
                {!isToday && (
                    <button
                        onClick={() => {
                            setDate(new Date());
                            setShowCalendar(false);
                        }}
                        className="text-xs text-primary hover:underline mb-3"
                        data-testid="staff-back-today"
                    >
                        ← Volver a hoy
                    </button>
                )}
                {showCalendar && (
                    <Card className="rounded-2xl border-border bg-card p-3 mb-4 inline-block">
                        <Calendar
                            mode="single"
                            selected={date}
                            onSelect={(d) => {
                                if (d) {
                                    setDate(d);
                                    setShowCalendar(false);
                                }
                            }}
                            weekStartsOn={1}
                        />
                    </Card>
                )}

                {/* Summary */}
                <Card className="rounded-2xl border-border bg-card p-4 mb-6 grid grid-cols-3 gap-3">
                    <div className="text-center" data-testid="staff-stat-total">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">
                            Total
                        </p>
                        <p className="font-heading text-2xl font-medium">{stats.total}</p>
                    </div>
                    <div className="text-center border-l border-border" data-testid="staff-stat-pending">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">
                            Pendientes
                        </p>
                        <p className="font-heading text-2xl font-medium text-primary">
                            {stats.pending}
                        </p>
                    </div>
                    <div className="text-center border-l border-border" data-testid="staff-stat-attended">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">
                            Atendidas
                        </p>
                        <p className="font-heading text-2xl font-medium text-foreground/60">
                            {stats.attended}
                        </p>
                    </div>
                </Card>

                {/* Share availability */}
                <AvailabilityShareCard dateISO={dateISO} />

                {/* List */}
                {loading ? (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                        Cargando...
                    </div>
                ) : appointments.length === 0 ? (
                    <Card
                        className="rounded-3xl border-border bg-card p-10 text-center soft-shadow"
                        data-testid="staff-empty"
                    >
                        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                            <Inbox className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <p className="font-heading text-lg mb-1">Sin citas agendadas</p>
                        <p className="text-sm text-muted-foreground">
                            No hay reservaciones para este día.
                        </p>
                    </Card>
                ) : (
                    <div className="space-y-3" data-testid="staff-appt-list">
                        {appointments.map((a) => {
                            const isUpdating = updatingIds.has(a.id);
                            const isAttended = a.attended;
                            const isNoShow = a.nurse === NURSE_NO_SHOW;
                            const isPreset = NURSE_OPTIONS.includes(a.nurse);
                            const isCustom = !!a.nurse && !isPreset && !isNoShow;
                            const selectValue = (() => {
                                if (isNoShow) return NURSE_NO_SHOW;
                                if (isPreset) return a.nurse;
                                if (isCustom) return NURSE_OTHER;
                                return "";
                            })();
                            const showingOtraInput =
                                selectValue === NURSE_OTHER || otraValues[a.id] !== undefined;
                            return (
                                <Card
                                    key={a.id}
                                    className={`rounded-2xl border bg-card p-4 transition-all ${
                                        isAttended
                                            ? "opacity-70 border-border"
                                            : isNoShow
                                            ? "opacity-70 border-destructive/30"
                                            : "border-border soft-shadow"
                                    }`}
                                    data-testid={`staff-appt-${a.id}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div
                                            className="shrink-0 mt-0.5 w-9 h-9 rounded-full flex items-center justify-center"
                                            aria-hidden="true"
                                        >
                                            {isUpdating ? (
                                                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                            ) : isAttended ? (
                                                <CheckCircle2 className="h-7 w-7 text-primary" strokeWidth={1.5} />
                                            ) : isNoShow ? (
                                                <UserX className="h-7 w-7 text-destructive" strokeWidth={1.5} />
                                            ) : (
                                                <Clock className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
                                            )}
                                        </div>

                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                <p className="font-heading text-lg font-medium text-primary tabular-nums">
                                                    {formatTime12(a.time)}
                                                </p>
                                                {isAttended && (
                                                    <span className="text-xs uppercase tracking-wider text-primary font-medium">
                                                        Atendida · {a.nurse}
                                                    </span>
                                                )}
                                                {isNoShow && (
                                                    <span className="text-xs uppercase tracking-wider text-destructive font-medium">
                                                        No se presentó
                                                    </span>
                                                )}
                                            </div>
                                            <p
                                                className={`font-medium text-base mb-1 ${
                                                    isAttended || isNoShow ? "line-through" : ""
                                                }`}
                                            >
                                                <User className="h-4 w-4 inline mr-1.5 text-muted-foreground" />
                                                {a.patient_name}
                                                <span className="inline-block align-middle ml-1">
                                                    <EditContactDialog
                                                        appt={a}
                                                        testIdPrefix="staff-edit"
                                                        onSave={(n, p, d, t) => updateContact(a, n, p, d, t)}
                                                    />
                                                </span>
                                            </p>
                                            <p className="text-sm text-muted-foreground mb-1">
                                                <Stethoscope className="h-3.5 w-3.5 inline mr-1.5" />
                                                {a.procedure_name}
                                            </p>
                                            {a.phone && (
                                                <div className="flex items-center gap-2 flex-wrap mb-3">
                                                    <a
                                                        href={`tel:${a.phone.replace(/\s/g, "")}`}
                                                        className="text-sm text-primary hover:underline"
                                                    >
                                                        <Phone className="h-3.5 w-3.5 inline mr-1.5" />
                                                        {a.phone}
                                                    </a>
                                                    {buildWhatsAppLink(a) && (
                                                        <a
                                                            href={buildWhatsAppLink(a)}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="inline-flex items-center gap-1 text-xs font-medium text-[#25D366] hover:underline"
                                                            title="Recordar/confirmar por WhatsApp"
                                                            data-testid={`staff-whatsapp-${a.id}`}
                                                        >
                                                            <MessageCircle className="h-3.5 w-3.5" />
                                                            WhatsApp
                                                        </a>
                                                    )}
                                                </div>
                                            )}

                                            {/* Nurse selector */}
                                            <div className="mt-3 pt-3 border-t border-border">
                                                <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1.5">
                                                    Atendida por
                                                </label>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <select
                                                        value={selectValue}
                                                        disabled={isUpdating}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            if (v === NURSE_OTHER) {
                                                                // Open inline custom input; do not call API yet
                                                                setOtraValues((prev) => ({
                                                                    ...prev,
                                                                    [a.id]: isCustom ? a.nurse : "",
                                                                }));
                                                            } else if (v === "") {
                                                                setOtraValues((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next[a.id];
                                                                    return next;
                                                                });
                                                                setNurse(a, null);
                                                            } else {
                                                                setOtraValues((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next[a.id];
                                                                    return next;
                                                                });
                                                                setNurse(a, v);
                                                            }
                                                        }}
                                                        className="h-9 rounded-lg border border-input bg-background px-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ring"
                                                        data-testid={`staff-nurse-select-${a.id}`}
                                                    >
                                                        <option value="">— Pendiente —</option>
                                                        {NURSE_OPTIONS.map((n) => (
                                                            <option key={n} value={n}>{n}</option>
                                                        ))}
                                                        <option value={NURSE_NO_SHOW}>No se presentó</option>
                                                        <option value={NURSE_OTHER}>Otra…</option>
                                                    </select>
                                                    {showingOtraInput && (
                                                        <>
                                                            <Input
                                                                value={otraValues[a.id] ?? (isCustom ? a.nurse : "")}
                                                                onChange={(e) =>
                                                                    setOtraValues((prev) => ({
                                                                        ...prev,
                                                                        [a.id]: e.target.value,
                                                                    }))
                                                                }
                                                                placeholder="Nombre"
                                                                maxLength={60}
                                                                className="h-9 w-40 rounded-lg text-sm"
                                                                data-testid={`staff-nurse-other-input-${a.id}`}
                                                            />
                                                            <Button
                                                                size="sm"
                                                                disabled={
                                                                    isUpdating ||
                                                                    !(otraValues[a.id] || "").trim()
                                                                }
                                                                onClick={() => {
                                                                    const v = (otraValues[a.id] || "").trim();
                                                                    if (v) {
                                                                        setOtraValues((prev) => {
                                                                            const next = { ...prev };
                                                                            delete next[a.id];
                                                                            return next;
                                                                        });
                                                                        setNurse(a, v);
                                                                    }
                                                                }}
                                                                className="h-9 rounded-lg"
                                                                data-testid={`staff-nurse-other-save-${a.id}`}
                                                            >
                                                                Guardar
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {lastUpdated && (
                    <p className="text-center text-xs text-muted-foreground mt-6 inline-flex items-center gap-1.5 w-full justify-center">
                        <Clock className="h-3 w-3" />
                        Actualizado a las {lastUpdated.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
                        · se actualiza solo cada minuto
                    </p>
                )}
            </main>
        </div>
    );
}

export default function StaffPage() {
    const navigate = useNavigate();
    const [pin, setPin] = useState(() => sessionStorage.getItem(PIN_STORAGE_KEY) || "");
    const [validating, setValidating] = useState(!!pin);

    // Validate stored PIN
    useEffect(() => {
        const validate = async () => {
            if (!pin) {
                setValidating(false);
                return;
            }
            try {
                await axios.post(`${API}/staff/login`, { pin });
                setValidating(false);
            } catch (err) {
                console.error("PIN validation failed:", err);
                sessionStorage.removeItem(PIN_STORAGE_KEY);
                setPin("");
                setValidating(false);
            }
        };
        validate();
    }, []);

    const handleLogout = () => {
        sessionStorage.removeItem(PIN_STORAGE_KEY);
        setPin("");
        toast.success("Sesión cerrada");
    };

    if (validating) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    if (!pin) {
        return <PinForm onSuccess={(p) => setPin(p)} />;
    }
    return <StaffList pin={pin} onLogout={handleLogout} />;
}
